/**
 * Element-Bibliothek für den Creator Studio
 * Lizenzfreie SVG-Elemente, organisiert nach Kategorien
 */

export interface LibraryElement {
  id: string;
  name: string;
  svg: string;
  tags: string[];
  // Default style overrides
  defaultWidth?: number;
  defaultHeight?: number;
  defaultFill?: string;
  defaultStroke?: string;
}

export interface ElementCategory {
  id: string;
  name: string;
  icon: string;
  elements: LibraryElement[];
}

// ===== BASIC SHAPES =====
const basicShapes: LibraryElement[] = [
  {
    id: 'shape-circle',
    name: 'Kreis',
    svg: '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="currentColor"/></svg>',
    tags: ['kreis', 'rund', 'circle'],
  },
  {
    id: 'shape-square',
    name: 'Quadrat',
    svg: '<svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" fill="currentColor"/></svg>',
    tags: ['quadrat', 'rechteck', 'square'],
  },
  {
    id: 'shape-rounded-rect',
    name: 'Abgerundetes Rechteck',
    svg: '<svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" rx="15" fill="currentColor"/></svg>',
    tags: ['rechteck', 'abgerundet', 'rounded'],
  },
  {
    id: 'shape-triangle',
    name: 'Dreieck',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 95,95 5,95" fill="currentColor"/></svg>',
    tags: ['dreieck', 'triangle'],
  },
  {
    id: 'shape-pentagon',
    name: 'Fünfeck',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 95,38 77,95 23,95 5,38" fill="currentColor"/></svg>',
    tags: ['fünfeck', 'pentagon'],
  },
  {
    id: 'shape-hexagon',
    name: 'Sechseck',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 93,27 93,73 50,95 7,73 7,27" fill="currentColor"/></svg>',
    tags: ['sechseck', 'hexagon', 'wabe'],
  },
  {
    id: 'shape-star-5',
    name: 'Stern 5-zackig',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 61,35 95,35 68,57 79,90 50,70 21,90 32,57 5,35 39,35" fill="currentColor"/></svg>',
    tags: ['stern', 'star', 'bewertung'],
  },
  {
    id: 'shape-star-4',
    name: 'Stern 4-zackig',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 60,40 95,50 60,60 50,95 40,60 5,50 40,40" fill="currentColor"/></svg>',
    tags: ['stern', 'star', 'sparkle'],
  },
  {
    id: 'shape-diamond',
    name: 'Raute',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 95,50 50,95 5,50" fill="currentColor"/></svg>',
    tags: ['raute', 'diamond', 'rhombus'],
  },
  {
    id: 'shape-heart',
    name: 'Herz',
    svg: '<svg viewBox="0 0 100 100"><path d="M50,88 C20,60 5,40 5,25 C5,10 20,5 32,5 C40,5 48,10 50,18 C52,10 60,5 68,5 C80,5 95,10 95,25 C95,40 80,60 50,88 Z" fill="currentColor"/></svg>',
    tags: ['herz', 'heart', 'liebe', 'love'],
  },
  {
    id: 'shape-cloud',
    name: 'Wolke',
    svg: '<svg viewBox="0 0 100 60"><path d="M80,55 C90,55 95,48 95,40 C95,32 88,25 78,25 C78,12 68,5 55,5 C42,5 32,15 32,25 C22,25 15,32 15,42 C15,52 22,55 32,55 Z" fill="currentColor"/></svg>',
    tags: ['wolke', 'cloud', 'wetter'],
    defaultHeight: 60,
  },
  {
    id: 'shape-oval',
    name: 'Oval',
    svg: '<svg viewBox="0 0 120 80"><ellipse cx="60" cy="40" rx="55" ry="35" fill="currentColor"/></svg>',
    tags: ['oval', 'ellipse'],
    defaultWidth: 120,
    defaultHeight: 80,
  },
];

// ===== ARROWS & POINTERS =====
const arrows: LibraryElement[] = [
  {
    id: 'arrow-right',
    name: 'Pfeil rechts',
    svg: '<svg viewBox="0 0 100 60"><polygon points="60,5 95,30 60,55 60,40 5,40 5,20 60,20" fill="currentColor"/></svg>',
    tags: ['pfeil', 'arrow', 'rechts', 'right'],
    defaultHeight: 60,
  },
  {
    id: 'arrow-left',
    name: 'Pfeil links',
    svg: '<svg viewBox="0 0 100 60"><polygon points="40,5 5,30 40,55 40,40 95,40 95,20 40,20" fill="currentColor"/></svg>',
    tags: ['pfeil', 'arrow', 'links', 'left'],
    defaultHeight: 60,
  },
  {
    id: 'arrow-up',
    name: 'Pfeil oben',
    svg: '<svg viewBox="0 0 60 100"><polygon points="30,5 55,40 40,40 40,95 20,95 20,40 5,40" fill="currentColor"/></svg>',
    tags: ['pfeil', 'arrow', 'oben', 'up'],
    defaultWidth: 60,
  },
  {
    id: 'arrow-down',
    name: 'Pfeil unten',
    svg: '<svg viewBox="0 0 60 100"><polygon points="30,95 55,60 40,60 40,5 20,5 20,60 5,60" fill="currentColor"/></svg>',
    tags: ['pfeil', 'arrow', 'unten', 'down'],
    defaultWidth: 60,
  },
  {
    id: 'arrow-curved',
    name: 'Gebogener Pfeil',
    svg: '<svg viewBox="0 0 100 80"><path d="M10,60 Q10,20 50,20 L50,10 L75,30 L50,50 L50,35 Q25,35 25,60 Z" fill="currentColor"/></svg>',
    tags: ['pfeil', 'arrow', 'gebogen', 'curved'],
    defaultHeight: 80,
  },
  {
    id: 'arrow-double',
    name: 'Doppelpfeil',
    svg: '<svg viewBox="0 0 100 50"><polygon points="25,5 45,25 25,45 25,32 5,32 5,18 25,18" fill="currentColor"/><polygon points="75,5 55,25 75,45 75,32 95,32 95,18 75,18" fill="currentColor"/></svg>',
    tags: ['pfeil', 'arrow', 'doppel', 'double'],
    defaultHeight: 50,
  },
  {
    id: 'chevron-right',
    name: 'Chevron rechts',
    svg: '<svg viewBox="0 0 60 100"><polyline points="15,10 45,50 15,90" fill="none" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    tags: ['chevron', 'pfeil', 'weiter'],
    defaultWidth: 60,
    defaultStroke: '#000000',
  },
  {
    id: 'pointer-hand',
    name: 'Zeigefinger',
    svg: '<svg viewBox="0 0 80 100"><path d="M40,5 L55,5 L55,35 L65,35 L65,45 L75,45 L75,75 L25,75 L25,55 L15,55 L15,35 L25,35 L25,25 L40,25 Z" fill="currentColor"/></svg>',
    tags: ['hand', 'zeiger', 'pointer', 'finger'],
    defaultWidth: 80,
  },
];

// ===== LINES & DIVIDERS =====
const lines: LibraryElement[] = [
  {
    id: 'line-simple',
    name: 'Einfache Linie',
    svg: '<svg viewBox="0 0 200 10"><line x1="5" y1="5" x2="195" y2="5" stroke="currentColor" stroke-width="3"/></svg>',
    tags: ['linie', 'line', 'teiler', 'divider'],
    defaultWidth: 200,
    defaultHeight: 10,
  },
  {
    id: 'line-dashed',
    name: 'Gestrichelte Linie',
    svg: '<svg viewBox="0 0 200 10"><line x1="5" y1="5" x2="195" y2="5" stroke="currentColor" stroke-width="3" stroke-dasharray="10,5"/></svg>',
    tags: ['linie', 'line', 'gestrichelt', 'dashed'],
    defaultWidth: 200,
    defaultHeight: 10,
  },
  {
    id: 'line-dotted',
    name: 'Gepunktete Linie',
    svg: '<svg viewBox="0 0 200 10"><line x1="5" y1="5" x2="195" y2="5" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-dasharray="1,12"/></svg>',
    tags: ['linie', 'line', 'punkte', 'dotted'],
    defaultWidth: 200,
    defaultHeight: 10,
  },
  {
    id: 'divider-wave',
    name: 'Wellen-Teiler',
    svg: '<svg viewBox="0 0 200 30"><path d="M0,15 Q25,5 50,15 T100,15 T150,15 T200,15" fill="none" stroke="currentColor" stroke-width="3"/></svg>',
    tags: ['welle', 'wave', 'teiler', 'divider'],
    defaultWidth: 200,
    defaultHeight: 30,
  },
  {
    id: 'divider-zigzag',
    name: 'Zickzack-Teiler',
    svg: '<svg viewBox="0 0 200 30"><polyline points="0,20 20,10 40,20 60,10 80,20 100,10 120,20 140,10 160,20 180,10 200,20" fill="none" stroke="currentColor" stroke-width="3"/></svg>',
    tags: ['zickzack', 'zigzag', 'teiler', 'divider'],
    defaultWidth: 200,
    defaultHeight: 30,
  },
  {
    id: 'divider-dots',
    name: 'Punkte-Teiler',
    svg: '<svg viewBox="0 0 200 20"><circle cx="10" cy="10" r="5" fill="currentColor"/><circle cx="50" cy="10" r="5" fill="currentColor"/><circle cx="90" cy="10" r="5" fill="currentColor"/><circle cx="130" cy="10" r="5" fill="currentColor"/><circle cx="170" cy="10" r="5" fill="currentColor"/></svg>',
    tags: ['punkte', 'dots', 'teiler', 'divider'],
    defaultWidth: 200,
    defaultHeight: 20,
  },
  {
    id: 'divider-ornament',
    name: 'Ornament-Teiler',
    svg: '<svg viewBox="0 0 200 20"><line x1="5" y1="10" x2="85" y2="10" stroke="currentColor" stroke-width="2"/><circle cx="100" cy="10" r="6" fill="currentColor"/><line x1="115" y1="10" x2="195" y2="10" stroke="currentColor" stroke-width="2"/></svg>',
    tags: ['ornament', 'teiler', 'divider', 'elegant'],
    defaultWidth: 200,
    defaultHeight: 20,
  },
];

// ===== FRAMES & BORDERS =====
const frames: LibraryElement[] = [
  {
    id: 'frame-simple',
    name: 'Einfacher Rahmen',
    svg: '<svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" fill="none" stroke="currentColor" stroke-width="4"/></svg>',
    tags: ['rahmen', 'frame', 'border'],
  },
  {
    id: 'frame-rounded',
    name: 'Abgerundeter Rahmen',
    svg: '<svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" rx="15" fill="none" stroke="currentColor" stroke-width="4"/></svg>',
    tags: ['rahmen', 'frame', 'border', 'abgerundet'],
  },
  {
    id: 'frame-double',
    name: 'Doppelter Rahmen',
    svg: '<svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" fill="none" stroke="currentColor" stroke-width="2"/><rect x="12" y="12" width="76" height="76" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
    tags: ['rahmen', 'frame', 'border', 'doppelt'],
  },
  {
    id: 'frame-dashed',
    name: 'Gestrichelter Rahmen',
    svg: '<svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" rx="5" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="8,4"/></svg>',
    tags: ['rahmen', 'frame', 'border', 'gestrichelt'],
  },
  {
    id: 'frame-circle',
    name: 'Kreisrahmen',
    svg: '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" stroke-width="4"/></svg>',
    tags: ['rahmen', 'frame', 'kreis', 'circle'],
  },
  {
    id: 'frame-ornate',
    name: 'Verzierter Rahmen',
    svg: '<svg viewBox="0 0 100 100"><rect x="10" y="10" width="80" height="80" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="10" cy="10" r="5" fill="currentColor"/><circle cx="90" cy="10" r="5" fill="currentColor"/><circle cx="10" cy="90" r="5" fill="currentColor"/><circle cx="90" cy="90" r="5" fill="currentColor"/></svg>',
    tags: ['rahmen', 'frame', 'verziert', 'ornate'],
  },
  {
    id: 'frame-ticket',
    name: 'Ticket-Rahmen',
    svg: '<svg viewBox="0 0 150 80"><path d="M10,5 L140,5 L140,30 Q130,35 130,40 Q130,45 140,50 L140,75 L10,75 L10,50 Q20,45 20,40 Q20,35 10,30 Z" fill="none" stroke="currentColor" stroke-width="3"/></svg>',
    tags: ['rahmen', 'ticket', 'coupon', 'gutschein'],
    defaultWidth: 150,
    defaultHeight: 80,
  },
];

// ===== BADGES & LABELS =====
const badges: LibraryElement[] = [
  {
    id: 'badge-circle',
    name: 'Kreis-Badge',
    svg: '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="currentColor"/></svg>',
    tags: ['badge', 'sticker', 'label', 'kreis'],
  },
  {
    id: 'badge-star',
    name: 'Stern-Badge',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 61,35 95,35 68,57 79,90 50,70 21,90 32,57 5,35 39,35" fill="currentColor"/></svg>',
    tags: ['badge', 'sticker', 'stern', 'star'],
  },
  {
    id: 'badge-ribbon',
    name: 'Banner-Badge',
    svg: '<svg viewBox="0 0 150 60"><polygon points="0,15 20,0 130,0 150,15 150,45 130,60 20,60 0,45" fill="currentColor"/></svg>',
    tags: ['badge', 'banner', 'ribbon', 'label'],
    defaultWidth: 150,
    defaultHeight: 60,
  },
  {
    id: 'badge-seal',
    name: 'Siegel-Badge',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 58,20 75,15 70,32 88,40 75,52 82,70 65,68 55,85 50,68 45,85 35,68 18,70 25,52 12,40 30,32 25,15 42,20" fill="currentColor"/></svg>',
    tags: ['badge', 'siegel', 'seal', 'qualität'],
  },
  {
    id: 'badge-corner',
    name: 'Ecken-Badge',
    svg: '<svg viewBox="0 0 100 100"><polygon points="0,0 100,0 100,35 35,100 0,100" fill="currentColor"/></svg>',
    tags: ['badge', 'ecke', 'corner', 'neu'],
  },
  {
    id: 'badge-pill',
    name: 'Pill-Badge',
    svg: '<svg viewBox="0 0 150 50"><rect x="0" y="0" width="150" height="50" rx="25" fill="currentColor"/></svg>',
    tags: ['badge', 'pill', 'tag', 'label'],
    defaultWidth: 150,
    defaultHeight: 50,
  },
  {
    id: 'badge-burst',
    name: 'Burst-Badge',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,0 56,20 70,5 65,26 85,18 75,38 98,38 80,50 98,62 75,62 85,82 65,74 70,95 56,80 50,100 44,80 30,95 35,74 15,82 25,62 2,62 20,50 2,38 25,38 15,18 35,26 30,5 44,20" fill="currentColor"/></svg>',
    tags: ['badge', 'burst', 'sale', 'aktion'],
  },
];

// ===== SOCIAL & ICONS =====
const socialIcons: LibraryElement[] = [
  {
    id: 'icon-like',
    name: 'Like / Daumen hoch',
    svg: '<svg viewBox="0 0 100 100"><path d="M30,95 L30,45 L15,45 L15,95 Z M40,95 L40,42 L50,20 C52,15 58,10 65,12 C72,14 72,22 70,28 L65,42 L85,42 C92,42 97,48 95,55 L88,88 C87,93 83,95 78,95 Z" fill="currentColor"/></svg>',
    tags: ['like', 'daumen', 'thumb', 'gut'],
  },
  {
    id: 'icon-heart',
    name: 'Herz / Like',
    svg: '<svg viewBox="0 0 100 100"><path d="M50,88 C20,60 5,40 5,25 C5,10 20,5 32,5 C40,5 48,10 50,18 C52,10 60,5 68,5 C80,5 95,10 95,25 C95,40 80,60 50,88 Z" fill="currentColor"/></svg>',
    tags: ['herz', 'heart', 'like', 'liebe'],
  },
  {
    id: 'icon-comment',
    name: 'Kommentar',
    svg: '<svg viewBox="0 0 100 100"><path d="M10,10 L90,10 L90,70 L55,70 L35,90 L35,70 L10,70 Z" fill="currentColor"/></svg>',
    tags: ['kommentar', 'comment', 'chat', 'nachricht'],
  },
  {
    id: 'icon-share',
    name: 'Teilen',
    svg: '<svg viewBox="0 0 100 100"><circle cx="75" cy="20" r="12" fill="currentColor"/><circle cx="75" cy="80" r="12" fill="currentColor"/><circle cx="25" cy="50" r="12" fill="currentColor"/><line x1="35" y1="45" x2="65" y2="25" stroke="currentColor" stroke-width="4"/><line x1="35" y1="55" x2="65" y2="75" stroke="currentColor" stroke-width="4"/></svg>',
    tags: ['teilen', 'share', 'senden'],
  },
  {
    id: 'icon-save',
    name: 'Speichern / Bookmark',
    svg: '<svg viewBox="0 0 80 100"><path d="M5,5 L75,5 L75,95 L40,70 L5,95 Z" fill="currentColor"/></svg>',
    tags: ['speichern', 'bookmark', 'merken', 'save'],
    defaultWidth: 80,
  },
  {
    id: 'icon-play',
    name: 'Play-Button',
    svg: '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="currentColor" opacity="0.9"/><polygon points="40,30 40,70 70,50" fill="white"/></svg>',
    tags: ['play', 'video', 'abspielen', 'start'],
  },
  {
    id: 'icon-check',
    name: 'Häkchen',
    svg: '<svg viewBox="0 0 100 100"><polyline points="20,55 40,75 80,25" fill="none" stroke="currentColor" stroke-width="12" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    tags: ['check', 'häkchen', 'fertig', 'ok'],
  },
  {
    id: 'icon-x',
    name: 'X / Schließen',
    svg: '<svg viewBox="0 0 100 100"><line x1="20" y1="20" x2="80" y2="80" stroke="currentColor" stroke-width="12" stroke-linecap="round"/><line x1="80" y1="20" x2="20" y2="80" stroke="currentColor" stroke-width="12" stroke-linecap="round"/></svg>',
    tags: ['x', 'schließen', 'close', 'nein'],
  },
  {
    id: 'icon-plus',
    name: 'Plus',
    svg: '<svg viewBox="0 0 100 100"><line x1="50" y1="15" x2="50" y2="85" stroke="currentColor" stroke-width="12" stroke-linecap="round"/><line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" stroke-width="12" stroke-linecap="round"/></svg>',
    tags: ['plus', 'hinzufügen', 'add', 'neu'],
  },
  {
    id: 'icon-star',
    name: 'Stern',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,5 61,35 95,35 68,57 79,90 50,70 21,90 32,57 5,35 39,35" fill="currentColor"/></svg>',
    tags: ['stern', 'star', 'bewertung', 'favorit'],
  },
  {
    id: 'icon-location',
    name: 'Standort',
    svg: '<svg viewBox="0 0 80 100"><path d="M40,5 C20,5 5,20 5,40 C5,65 40,95 40,95 C40,95 75,65 75,40 C75,20 60,5 40,5 Z M40,55 C30,55 22,47 22,37 C22,27 30,19 40,19 C50,19 58,27 58,37 C58,47 50,55 40,55 Z" fill="currentColor"/></svg>',
    tags: ['standort', 'location', 'pin', 'ort'],
    defaultWidth: 80,
  },
  {
    id: 'icon-clock',
    name: 'Uhr',
    svg: '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" stroke-width="6"/><line x1="50" y1="50" x2="50" y2="25" stroke="currentColor" stroke-width="5" stroke-linecap="round"/><line x1="50" y1="50" x2="70" y2="60" stroke="currentColor" stroke-width="5" stroke-linecap="round"/></svg>',
    tags: ['uhr', 'clock', 'zeit', 'time'],
  },
];

// ===== DECORATIVE =====
const decorative: LibraryElement[] = [
  {
    id: 'deco-sparkle',
    name: 'Funkeln',
    svg: '<svg viewBox="0 0 100 100"><polygon points="50,0 55,40 100,50 55,60 50,100 45,60 0,50 45,40" fill="currentColor"/></svg>',
    tags: ['funkeln', 'sparkle', 'glitzer', 'stern'],
  },
  {
    id: 'deco-sparkles-3',
    name: 'Funkeln Gruppe',
    svg: '<svg viewBox="0 0 100 100"><polygon points="25,10 27,20 35,22 27,24 25,35 23,24 15,22 23,20" fill="currentColor"/><polygon points="70,30 74,50 95,55 74,60 70,80 66,60 45,55 66,50" fill="currentColor"/><polygon points="30,60 32,70 42,72 32,74 30,85 28,74 18,72 28,70" fill="currentColor"/></svg>',
    tags: ['funkeln', 'sparkles', 'glitzer', 'sterne'],
  },
  {
    id: 'deco-confetti',
    name: 'Konfetti',
    svg: '<svg viewBox="0 0 100 100"><rect x="15" y="20" width="8" height="15" fill="currentColor" transform="rotate(30 19 27)"/><rect x="45" y="10" width="6" height="12" fill="currentColor" transform="rotate(-20 48 16)"/><rect x="75" y="25" width="7" height="14" fill="currentColor" transform="rotate(45 78 32)"/><rect x="25" y="55" width="5" height="10" fill="currentColor" transform="rotate(-35 27 60)"/><rect x="60" y="50" width="8" height="16" fill="currentColor" transform="rotate(15 64 58)"/><rect x="85" y="60" width="6" height="12" fill="currentColor" transform="rotate(-10 88 66)"/><rect x="35" y="80" width="7" height="14" fill="currentColor" transform="rotate(25 38 87)"/><rect x="70" y="75" width="5" height="10" fill="currentColor" transform="rotate(-40 72 80)"/></svg>',
    tags: ['konfetti', 'confetti', 'party', 'feier'],
  },
  {
    id: 'deco-ribbon-curl',
    name: 'Schleifenband',
    svg: '<svg viewBox="0 0 100 80"><path d="M10,40 Q25,20 40,40 Q55,60 70,40 Q85,20 95,30" fill="none" stroke="currentColor" stroke-width="8" stroke-linecap="round"/></svg>',
    tags: ['schleife', 'ribbon', 'band', 'deko'],
    defaultHeight: 80,
  },
  {
    id: 'deco-quote-left',
    name: 'Anführungszeichen links',
    svg: '<svg viewBox="0 0 100 80"><path d="M10,50 Q10,25 30,25 Q20,25 20,40 Q20,55 35,55 Q35,70 20,70 Q10,70 10,50 Z" fill="currentColor"/><path d="M55,50 Q55,25 75,25 Q65,25 65,40 Q65,55 80,55 Q80,70 65,70 Q55,70 55,50 Z" fill="currentColor"/></svg>',
    tags: ['zitat', 'quote', 'anführung'],
    defaultHeight: 80,
  },
  {
    id: 'deco-quote-right',
    name: 'Anführungszeichen rechts',
    svg: '<svg viewBox="0 0 100 80"><path d="M90,30 Q90,55 70,55 Q80,55 80,40 Q80,25 65,25 Q65,10 80,10 Q90,10 90,30 Z" fill="currentColor"/><path d="M45,30 Q45,55 25,55 Q35,55 35,40 Q35,25 20,25 Q20,10 35,10 Q45,10 45,30 Z" fill="currentColor"/></svg>',
    tags: ['zitat', 'quote', 'anführung'],
    defaultHeight: 80,
  },
  {
    id: 'deco-brush-stroke',
    name: 'Pinselstrich',
    svg: '<svg viewBox="0 0 200 40"><path d="M5,20 Q30,5 60,22 Q90,38 120,18 Q150,0 180,25 Q190,30 195,20" fill="none" stroke="currentColor" stroke-width="12" stroke-linecap="round"/></svg>',
    tags: ['pinsel', 'brush', 'strich', 'unterstreichen'],
    defaultWidth: 200,
    defaultHeight: 40,
  },
  {
    id: 'deco-scribble-circle',
    name: 'Kritzel-Kreis',
    svg: '<svg viewBox="0 0 100 100"><ellipse cx="50" cy="50" rx="42" ry="38" fill="none" stroke="currentColor" stroke-width="4" transform="rotate(-5 50 50)"/><ellipse cx="50" cy="50" rx="38" ry="42" fill="none" stroke="currentColor" stroke-width="3" transform="rotate(10 50 50)"/></svg>',
    tags: ['kreis', 'kritzel', 'scribble', 'highlight'],
  },
  {
    id: 'deco-highlight',
    name: 'Textmarker',
    svg: '<svg viewBox="0 0 200 30"><rect x="5" y="8" width="190" height="14" rx="3" fill="currentColor" opacity="0.5"/></svg>',
    tags: ['highlight', 'textmarker', 'markierung'],
    defaultWidth: 200,
    defaultHeight: 30,
  },
];

// ===== BUTTONS =====
const buttons: LibraryElement[] = [
  {
    id: 'btn-rounded',
    name: 'Button abgerundet',
    svg: '<svg viewBox="0 0 180 60"><rect x="2" y="2" width="176" height="56" rx="28" fill="currentColor"/></svg>',
    tags: ['button', 'cta', 'klick'],
    defaultWidth: 180,
    defaultHeight: 60,
  },
  {
    id: 'btn-rectangle',
    name: 'Button eckig',
    svg: '<svg viewBox="0 0 180 60"><rect x="2" y="2" width="176" height="56" rx="8" fill="currentColor"/></svg>',
    tags: ['button', 'cta', 'klick'],
    defaultWidth: 180,
    defaultHeight: 60,
  },
  {
    id: 'btn-outline',
    name: 'Button Outline',
    svg: '<svg viewBox="0 0 180 60"><rect x="4" y="4" width="172" height="52" rx="26" fill="none" stroke="currentColor" stroke-width="4"/></svg>',
    tags: ['button', 'cta', 'outline'],
    defaultWidth: 180,
    defaultHeight: 60,
  },
  {
    id: 'btn-arrow',
    name: 'Button mit Pfeil',
    svg: '<svg viewBox="0 0 200 60"><rect x="2" y="2" width="196" height="56" rx="8" fill="currentColor"/><polyline points="160,22 175,30 160,38" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    tags: ['button', 'cta', 'pfeil', 'arrow'],
    defaultWidth: 200,
    defaultHeight: 60,
  },
  {
    id: 'btn-circle',
    name: 'Runder Button',
    svg: '<svg viewBox="0 0 80 80"><circle cx="40" cy="40" r="38" fill="currentColor"/></svg>',
    tags: ['button', 'rund', 'circle'],
    defaultWidth: 80,
    defaultHeight: 80,
  },
];

// ===== EXPORT CATEGORIES =====
export const ELEMENT_CATEGORIES: ElementCategory[] = [
  {
    id: 'shapes',
    name: 'Formen',
    icon: '⬡',
    elements: basicShapes,
  },
  {
    id: 'arrows',
    name: 'Pfeile',
    icon: '→',
    elements: arrows,
  },
  {
    id: 'lines',
    name: 'Linien & Teiler',
    icon: '─',
    elements: lines,
  },
  {
    id: 'frames',
    name: 'Rahmen',
    icon: '▢',
    elements: frames,
  },
  {
    id: 'badges',
    name: 'Badges & Labels',
    icon: '🏷️',
    elements: badges,
  },
  {
    id: 'social',
    name: 'Social & Icons',
    icon: '❤️',
    elements: socialIcons,
  },
  {
    id: 'decorative',
    name: 'Dekorativ',
    icon: '✨',
    elements: decorative,
  },
  {
    id: 'buttons',
    name: 'Buttons',
    icon: '🔘',
    elements: buttons,
  },
];

// Helper to get all elements flat
export function getAllElements(): LibraryElement[] {
  return ELEMENT_CATEGORIES.flatMap(cat => cat.elements);
}

// Helper to search elements
export function searchElements(query: string): LibraryElement[] {
  const q = query.toLowerCase();
  return getAllElements().filter(el =>
    el.name.toLowerCase().includes(q) ||
    el.tags.some(tag => tag.toLowerCase().includes(q))
  );
}

// Helper to get element by ID
export function getElementById(id: string): LibraryElement | undefined {
  return getAllElements().find(el => el.id === id);
}
